<template>
	<div class="account-changer">
		<div class="account-preview-wrapper">
			<button class="account-preview" @click.prevent="handleIconOpen">
				<div class="account-profile-image">
					<p>
						{{ getFirstLetter(profileName) }}
					</p>
				</div>
			</button>
		</div>
		<div
			class="account-selector-wrapper"
			:class="[{ 'selector-open': open }, { visible: open }]"
		>
			<label v-if="label" class="selector-label">{{ label }}</label>
			<UiSelect
				ref="selectElement"
				class="account-selector"
				:offset-y="8"
				:loading="loading"
				:options="options"
				:model-value="modelValue"
				@update:model-value="handleSelection"
				@before-show="setOpen(true)"
				@before-hide="setOpen(false)"
			/>
			<div v-if="error" class="error-message">
				{{ error }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue';

type AuthAccountSelectorProps = {
	open?: boolean;
	label?: string;
	profileName?: string;
	options: string[];
	modelValue?: string;
	loading?: boolean;
	error?: string | null;
};

defineProps<AuthAccountSelectorProps>();

const emit = defineEmits<{
	'update:modelValue': [value: string];
	'update:open': [value: boolean];
	'selecting-account': [value: boolean];
}>();

function handleSelection(value: unknown) {
	// Only real option selections may travel on the modelValue channel. The
	// nuxt-library predecessor leaked its open/closed boolean through
	// update:modelValue, which corrupted the selected-account state upstream.
	if (typeof value !== 'string' || !value) return;
	emit('update:modelValue', value);
}

function setOpen(value: boolean) {
	emit('update:open', value);
	emit('selecting-account', value);
}

function getFirstLetter(name: string | undefined): string {
	if (!name) {
		return '';
	}

	return name.charAt(0).toUpperCase();
}

const selectElement = useTemplateRef<{ show?: () => void }>('selectElement');

function handleIconOpen() {
	setOpen(true);
	selectElement.value?.show?.();
}
</script>

<style lang="scss" scoped>
.account-changer {
	display: flex;
	flex-direction: row;
	align-items: center;
}
.account-selector {
	width: 100%;
	max-width: 120px;
}
.selector-label {
	display: block;
	margin-bottom: 0.5rem;
	font-weight: 500;
}
.account-select {
	width: 100%;
	padding: 0.5rem;
	border: 1px solid #ccc;
	border-radius: 4px;
	font-size: 1rem;
}
.error-message {
	color: #e53e3e;
	margin-top: 0.5rem;
	font-size: 0.875rem;
}
.no-accounts {
	margin-top: 0.5rem;
	font-size: 0.875rem;
	color: #718096;
}

.account-preview-wrapper {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 44px;
	height: 44px;
	border: 1px solid var(--background-300);
	aspect-ratio: 1;
	border-radius: 50%;
	background-color: var(--background-200);

	.account-preview {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		border: none;
		background: none;
		cursor: pointer;
		padding: 0;
		color: var(--text-50);
	}
}

.account-selector-wrapper {
	display: flex;
	flex-direction: column;
	margin-left: 0rem;
	opacity: 0;
	min-width: 120px;
	transition:
		opacity 0.35s ease,
		margin-left 0.35s ease;

	&.selector-open {
		opacity: 1;
		margin-left: 0.5rem;
	}

	&.visible {
		opacity: 1;
		margin-left: 0.5rem;
	}
}

@media (max-width: 768px) {
	.account-preview-wrapper {
		width: 38px;
		height: 38px;
		position: relative;
		top: 0px;
	}
}
</style>
